import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../api/axios';
import { CadreAcces, EnTeteAcces } from '../components/CadreAcces';
import { Chargement, Message } from '../components/charte/Elements';
import ReglesMotDePasse from '../components/ReglesMotDePasse';
import { motDePasseConforme, reglesMotDePasse } from '../auth/motDePasse';
import { jourCourt } from '../donnees/situation';
import { messageInscription } from '../utils/messagesErreur';

/* Création de compte, sur invitation uniquement (user-service, a370081).

   - Sans code : page qui explique l'accès sur invitation, avec un contact.
   - Avec ?invitation=<code> : le code est vérifié par
     GET /user/client-invitation?code= avant d'afficher le formulaire. Le
     serveur renvoie l'adresse et l'organisation de l'invitation : l'adresse
     n'est pas modifiable (le compte lui est réservé), l'organisation fixée
     par Tribe non plus.
   - Envoi : POST /user/client-register avec le code dans le corps. L'adresse
     étant confirmée par l'invitation, la connexion suit directement. */

const SUJET_ACCES = encodeURIComponent("Demande d'accès au portail client Tribe");
const SUJET_NOUVELLE = encodeURIComponent('Nouvelle invitation au portail client Tribe');

interface Invitation { email: string; organisation: string | null; expireLe?: string }

type EtatInvitation =
  | { etat: 'verification' }
  | { etat: 'valide'; invitation: Invitation }
  | { etat: 'inconnue' }
  | { etat: 'perimee' }
  | { etat: 'injoignable' };

const AccesSurInvitation: React.FC = () => (
  <CadreAcces>
    <EnTeteAcces etiquette="Portail client" titre="Accès sur invitation" />
    <div className="pile" style={{ fontSize: '15px', lineHeight: 1.55 }}>
      <p>
        Le portail client Tribe est réservé aux organisations qui commandent des missions de collecte.
        Chaque compte est ouvert par l'équipe Tribe, qui vous envoie un lien d'invitation par email.
      </p>
      <p>
        Pour demander un accès, écrivez à{' '}
        <a href={`mailto:contact@tribe.sn?subject=${SUJET_ACCES}`}>contact@tribe.sn</a>{' '}
        en indiquant votre nom, votre organisation et votre besoin.
      </p>
      <p className="texte-secondaire petit">
        Vous avez reçu une invitation ? Ouvrez le lien contenu dans l'email : il mène directement au formulaire de création de compte.
      </p>
      <Link to="/connexion" className="bouton bouton--secondaire bouton--large">J'ai déjà un compte</Link>
    </div>
  </CadreAcces>
);

const InvitationInvalide: React.FC<{ titre: string; children: React.ReactNode }> = ({ titre, children }) => (
  <CadreAcces>
    <EnTeteAcces etiquette="Portail client" titre={titre} />
    <div className="pile" style={{ fontSize: '15px', lineHeight: 1.55 }}>
      {children}
      <p>
        Pour recevoir une nouvelle invitation, écrivez à{' '}
        <a href={`mailto:contact@tribe.sn?subject=${SUJET_NOUVELLE}`}>contact@tribe.sn</a>.
      </p>
      <Link to="/connexion" className="bouton bouton--secondaire bouton--large">J'ai déjà un compte</Link>
    </div>
  </CadreAcces>
);

const Register: React.FC = () => {
  const [params] = useSearchParams();
  const code = (params.get('invitation') ?? '').trim();
  const [verif, setVerif] = useState<EtatInvitation>({ etat: 'verification' });
  const [essai, setEssai] = useState(0);

  useEffect(() => {
    if (!code) return;
    let actif = true;
    setVerif({ etat: 'verification' });
    userApi.get('/user/client-invitation', { params: { code } })
      .then(res => { if (actif) setVerif({ etat: 'valide', invitation: res.data }); })
      .catch(err => {
        if (!actif) return;
        const s = err?.response?.status;
        setVerif(s === 404 ? { etat: 'inconnue' } : s === 410 ? { etat: 'perimee' } : { etat: 'injoignable' });
      });
    return () => { actif = false; };
  }, [code, essai]);

  if (!code) return <AccesSurInvitation />;

  switch (verif.etat) {
    case 'verification':
      return <CadreAcces><Chargement texte="Vérification de votre invitation…" /></CadreAcces>;
    case 'inconnue':
      return (
        <InvitationInvalide titre="Invitation introuvable">
          <p>Ce lien d'invitation n'est pas reconnu. Vérifiez qu'il a été copié en entier depuis l'email reçu.</p>
        </InvitationInvalide>
      );
    case 'perimee':
      return (
        <InvitationInvalide titre="Invitation plus valable">
          <p>
            Ce lien d'invitation ne peut plus servir : il était valable 7 jours, il a peut-être déjà servi à créer
            votre compte, ou il a été annulé par Tribe.
          </p>
          <p>Si vous avez déjà créé votre compte, connectez-vous.</p>
        </InvitationInvalide>
      );
    case 'injoignable':
      return (
        <CadreAcces>
          <EnTeteAcces etiquette="Portail client" titre="Création de compte" />
          <div className="pile">
            <Message genre="erreur">Votre invitation n'a pas pu être vérifiée. Vérifiez votre connexion puis réessayez.</Message>
            <button type="button" className="bouton" onClick={() => setEssai(e => e + 1)}>Réessayer</button>
          </div>
        </CadreAcces>
      );
    case 'valide':
      return <FormulaireInscription code={code} invitation={verif.invitation} />;
  }
};

const FormulaireInscription: React.FC<{ code: string; invitation: Invitation }> = ({ code, invitation }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    organisation: invitation.organisation ?? '',
    password: '',
    confirm: '',
  });
  const [erreur, setErreur] = useState<React.ReactNode>('');
  const [envoi, setEnvoi] = useState(false);
  const [accepte, setAccepte] = useState(false);

  const set = (champ: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [champ]: e.target.value }));

  const valide = !!form.prenom.trim() && !!form.nom.trim() && motDePasseConforme(form.password)
    && form.password === form.confirm && accepte;

  const envoyer = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (form.password !== form.confirm) { setErreur('Les deux mots de passe ne correspondent pas.'); return; }
    setEnvoi(true);
    try {
      await register({
        email: invitation.email,
        password: form.password,
        nom: `${form.prenom} ${form.nom}`.trim(),
        organisation: form.organisation.trim(),
        invitation: code,
      });
      navigate('/connexion', { state: { compteCree: invitation.email } });
    } catch (err: any) {
      const s = err?.response?.status;
      setErreur(s === 409
        ? <>Un compte existe déjà avec cette adresse. <Link to="/connexion">Connectez-vous</Link>, ou utilisez « Mot de passe oublié ».</>
        : messageInscription(err));
    } finally {
      setEnvoi(false);
    }
  };

  const regles = reglesMotDePasse(form.password);
  const accroche = (
    <>
      <div className="etiquette-mono" style={{ color: 'var(--ocre-clair)', letterSpacing: '0.18em' }}>Vous êtes invité</div>
      <p className="acces__accroche acces__accroche--invitation">
        Tribe a ouvert un accès pour{' '}
        <span style={{ color: 'var(--orange-clair)' }}>{invitation.organisation || 'votre organisation'}</span>.
      </p>
      <p className="acces__texte">
        Cette invitation est personnelle, liée à votre adresse, et valable 7 jours. Il ne vous reste qu'à indiquer votre nom et
        choisir un mot de passe.
      </p>
    </>
  );

  return (
    <CadreAcces accroche={accroche} pied={invitation.expireLe ? <span>INVITATION VALABLE JUSQU'AU {jourCourt(invitation.expireLe).toUpperCase()}</span> : undefined}>
      <EnTeteAcces etiquette="Portail client" titre="Créer votre compte" />

      {erreur && <Message genre="erreur">{erreur}</Message>}

      <form onSubmit={envoyer} className="pile" style={{ gap: '22px' }}>
        <div className="pile" style={{ gap: '14px' }}>
          <div className="champ">
            <span id="inscription-email-libelle" className="champ__libelle">Adresse e-mail</span>
            <div className="valeur-fixe" aria-labelledby="inscription-email-libelle">
              <span>{invitation.email}</span>
              <span className="marque-confirmee">
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
                Invitée
              </span>
            </div>
          </div>

          <div className="grille-2">
            <div className="champ">
              <label htmlFor="inscription-prenom">Prénom</label>
              <input id="inscription-prenom" autoComplete="given-name" value={form.prenom} onChange={set('prenom')} required />
            </div>
            <div className="champ">
              <label htmlFor="inscription-nom">Nom</label>
              <input id="inscription-nom" autoComplete="family-name" value={form.nom} onChange={set('nom')} required />
            </div>
          </div>
          <p className="champ__aide" style={{ marginTop: '-6px' }}>Tels qu'ils apparaîtront sur vos documents.</p>

          <div className="champ">
            <label htmlFor="inscription-organisation">Organisation</label>
            <input id="inscription-organisation" autoComplete="organization" value={form.organisation} onChange={set('organisation')}
              readOnly={!!invitation.organisation} placeholder="Ministère, ONG, entreprise…" />
          </div>

          <div className="champ">
            <label htmlFor="inscription-mdp">Mot de passe</label>
            <input id="inscription-mdp" type="password" autoComplete="new-password" value={form.password} onChange={set('password')}
              aria-describedby="inscription-mdp-regles" placeholder="8 caractères minimum" required />
            <div className="jauge-mdp" aria-hidden>
              {regles.map(r => <span key={r.libelle} style={{ background: r.ok ? 'var(--vert)' : undefined }} />)}
            </div>
            <ReglesMotDePasse id="inscription-mdp-regles" motDePasse={form.password} />
          </div>

          <div className="champ">
            <label htmlFor="inscription-confirmation">Confirmer le mot de passe</label>
            <input id="inscription-confirmation" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} required />
          </div>

          <label className="case" style={{ fontSize: '13px', color: 'var(--texte-2)' }}>
            <input type="checkbox" checked={accepte} onChange={e => setAccepte(e.target.checked)} />
            <span>J'accepte les <a href="/legal" style={{ fontWeight: 600 }}>conditions d'utilisation</a> du portail et la politique de confidentialité.</span>
          </label>
        </div>

        <button type="submit" className="bouton bouton--large" disabled={envoi || !valide}>
          {envoi ? 'Création…' : 'Créer mon compte'}
        </button>
      </form>

      <div className="carte-acces__pied">
        <p>
          Lien expiré ou adresse incorrecte ? <a href={`mailto:contact@tribe.sn?subject=${SUJET_NOUVELLE}`} style={{ fontWeight: 600 }}>Demandez une nouvelle invitation</a>{' '}
          à la personne qui vous l'a envoyée, ou à contact@tribe.sn.
        </p>
        <p><Link to="/connexion" style={{ fontWeight: 600 }}>J'ai déjà un compte</Link></p>
      </div>
    </CadreAcces>
  );
};

export default Register;
